import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Loader2,
  User as UserIcon,
  RefreshCw,
  Clock,
  Brain,
  MessageSquare,
  Trash2,
  Copy,
  FileText,
  ChevronRight,
  ChevronLeft,
  Search,
  Hash,
} from 'lucide-react';
import {
  A2UISurface,
  A2UISurfaceRenderer,
  ChatMessage,
  ThreadStats,
  computeThreadStats,
  ChatMessageBubble,
} from '../components/ChatComponents';
import VirtualizedMessageList from '../components/playground/VirtualizedMessageList';
import ThoughtsPanel from '../components/playground/ThoughtsPanel';
import CalendarTabPanel from '../components/CalendarTabPanel';
import {
  getTierStyle,
  TabBar,
  ErrorBanner,
  InfoTabPanel,
  ThreadInfoPanel,
  MemoriesTabPanel,
  DocumentsTabPanel,
  HealthTabPanel,
} from '../components/TabPanels';
import { formatDateKo } from '../utils/date';
import type { TabId } from '../components/TabPanels';
import { usersApi, UserDetail, UserListItem } from '../api/users';
import { threadsApi, ThreadListItem } from '../api/threads';
import { useAuth } from '../contexts/AuthContext';
import { useAgentUserStats, useProfileEditor, useTierManager, useTabData, useThreadDetail } from '../hooks';
import { useAvailableTools } from '../hooks/useAvailableTools';
import { AvailableToolsSection } from '../components/AvailableToolsSection';

type Message = ChatMessage;

const AgentUsers: React.FC = () => {
  // URL search params (persist navigation state across refresh/sharing)
  const [searchParams, setSearchParams] = useSearchParams();
  const userIdFromUrl = searchParams.get('userId');
  const threadIdFromUrl = searchParams.get('threadId');
  const tabFromUrl = searchParams.get('tab') as TabId | null;
  const pageFromUrl = searchParams.get('page');
  const sourceFromUrl = searchParams.get('source') as 'all' | 'playground' | 'real' | null;

  const paramsRef = React.useRef<Record<string, string>>({});
  paramsRef.current = Object.fromEntries(searchParams.entries());

  const updateUrlParams = (updates: Record<string, string | null>) => {
    const merged = { ...paramsRef.current, ...updates };
    const params: Record<string, string> = {};
    for (const [k, v] of Object.entries(merged)) {
      if (v) params[k] = v;
    }
    paramsRef.current = params;
    setSearchParams(params, { replace: true });
  };

  // User list state (server-side pagination)
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [page, setPageState] = useState(pageFromUrl ? parseInt(pageFromUrl, 10) : 1);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const pageSize = 20;

  const setPage = (p: number) => {
    setPageState(p);
    updateUrlParams({ page: p > 1 ? String(p) : null });
  };

  const [selectedUserId, setSelectedUserIdState] = useState<string | null>(userIdFromUrl);
  const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null);

  // Thread state
  const [threads, setThreads] = useState<ThreadListItem[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [selectedThreadId, setSelectedThreadIdState] = useState<string | null>(threadIdFromUrl);

  // Chat state (read-only)
  const [messages, setMessages] = useState<Message[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Archive progressive loading state
  const [archiveOffset, setArchiveOffset] = useState(0);
  const [hasMoreArchive, setHasMoreArchive] = useState(false);
  const [loadingArchive, setLoadingArchive] = useState(false);
  const isLoadingArchiveRef = useRef(false);

  // Helper: sessionStorage cache key for archive messages
  const archiveCacheKey = (threadId: string) => `archive_msgs_${threadId}`;
  const archiveMetaCacheKey = (threadId: string) => `archive_meta_${threadId}`;

  // Thread summary state
  const [threadSummary, setThreadSummary] = useState<string | null>(null);
  const [threadStats, setThreadStats] = useState<ThreadStats | null>(null);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [generationStatus, setGenerationStatus] = useState<string | null>(null);
  const [archiveTotalState, setArchiveTotalState] = useState(0);
  const { data: threadDetail, isLoading: threadDetailLoading, refetch: refetchThreadDetail } = useThreadDetail(selectedThreadId);

  // Tab state
  const [activeTab, setActiveTabState] = useState<TabId>(tabFromUrl || 'chat');

  // User delete state
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [cloningUserId, setCloningUserId] = useState<string | null>(null);
  const { user: adminUser } = useAuth();

  // Source filter state
  const [sourceFilter, setSourceFilterState] = useState<'all' | 'playground' | 'real'>(sourceFromUrl || 'real');

  // Stats
  const { data: stats, refetch: refetchStats } = useAgentUserStats();

  // Available tools (info 탭)
  const { availableTools, toolsLoading, loadAvailableTools } = useAvailableTools();

  const totalPages = Math.ceil(total / pageSize);

  // Load users with pagination
  const loadUsers = async (p?: number) => {
    const currentPage = p ?? page;
    try {
      setUsersLoading(true);
      const offset = (currentPage - 1) * pageSize;
      const response = await usersApi.list({
        limit: pageSize,
        offset,
        search: searchTerm || undefined,
        source: sourceFilter !== 'all' ? sourceFilter : undefined,
      });
      setUsers(response.users);
      setTotal(response.pagination.total);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    tier.loadTiers();
  }, [page]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      loadUsers(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Re-load when source filter changes
  useEffect(() => {
    setPage(1);
    loadUsers(1);
  }, [sourceFilter]);

  // Load user details and related data when user selected
  useEffect(() => {
    if (selectedUserId) {
      // 필수: 항상 로드
      loadUserDetails(selectedUserId);
      loadThreads(selectedUserId);
      // 탭 데이터 초기화
      tabData.setMemories([]);
      tabData.setDocuments([]);
      tabData.setHealthAnalyses([]);
      // 현재 활성 탭이면 해당 데이터만 로드
      if (activeTab === 'memories') tabData.loadUserMemories(selectedUserId);
      if (activeTab === 'documents') tabData.loadUserDocuments(selectedUserId);
      if (activeTab === 'health') tabData.loadHealthAnalyses(selectedUserId);
      if (activeTab === 'info') loadAvailableTools();
    }
  }, [selectedUserId]);

  // Load messages when thread selected
  useEffect(() => {
    if (selectedThreadId) {
      loadThreadMessages(selectedThreadId);
    } else {
      setMessages([]);
      setThreadSummary(null);
      setThreadStats(null);
      setHasMoreArchive(false);
      setArchiveOffset(0);
    }
  }, [selectedThreadId]);

  // URL-synced state setters
  const setSelectedUserId = (userId: string | null) => {
    setSelectedUserIdState(userId);
    updateUrlParams({ userId, threadId: null, tab: null, page: null });
  };

  const setSelectedThreadId = (threadId: string | null) => {
    setSelectedThreadIdState(threadId);
    updateUrlParams({ threadId });
  };

  const setActiveTab = (tab: TabId) => {
    setActiveTabState(tab);
    updateUrlParams({ tab: tab !== 'chat' ? tab : null });
  };

  const setSourceFilter = (source: 'all' | 'playground' | 'real') => {
    setSourceFilterState(source);
    updateUrlParams({ source: source !== 'real' ? source : null, page: null });
  };

  const handleSelectUser = (userId: string) => {
    setSelectedUserIdState(userId);
    setSelectedThreadIdState(null);
    setActiveTabState('chat');
    updateUrlParams({ userId, threadId: null, tab: null });
    setMessages([]);
    tabData.setMemories([]);
    tabData.setDocuments([]);
    tabData.setHealthAnalyses([]);
    tabData.setExpandedAnalysisId(null);
    tabData.setExpandedAnalysisDetail(null);
    profile.setIsEditingProfile(false);
    profile.setEditProfileJson('');
  };

  const loadUserDetails = async (userId: string) => {
    try {
      const user = await usersApi.getById(userId);
      setSelectedUser(user);
    } catch (err) {
      console.error('Failed to load user details:', err);
    }
  };

  // Custom hooks (placed after loadUserDetails to avoid TDZ)
  const profile = useProfileEditor({ selectedUser, selectedUserId, onProfileSaved: loadUserDetails, onError: setError });
  const tier = useTierManager({ selectedUserId, onTierChanged: loadUserDetails, onError: setError });
  const tabData = useTabData({ selectedUserId, onError: setError });

  const loadThreads = async (userId: string) => {
    try {
      setThreadsLoading(true);
      const response = await threadsApi.listByUser(userId, { limit: 50, order: 'desc' });
      setThreads(response.threads);
      // Auto-select first thread
      if (response.threads.length > 0) {
        setSelectedThreadId(response.threads[0].thread_id);
      }
    } catch (err) {
      console.error('Failed to load threads:', err);
      setThreads([]);
    } finally {
      setThreadsLoading(false);
    }
  };

  const loadThreadMessages = async (threadId: string) => {
    try {
      // admin context — 항상 thoughts opt-in (운영자 prompt 폭주 패턴 분석용)
      const response = await threadsApi.getMessages(threadId, {
        order: 'asc',
        source: 'active',
        limit: 1000,
        include: 'thoughts',
      });
      const activeMessages: Message[] = response.messages
        .filter((msg) => ['human', 'user', 'ai', 'assistant'].includes(msg.role) && (msg.content || '').trim())
        .map((msg, index) => ({
          id: msg.id || `msg-${index}`,
          role: msg.role === 'human' || msg.role === 'user' ? 'user' : 'assistant',
          content: (msg.content || '').trim(),
          timestamp: new Date(msg.timestamp),
          source: msg.source,
          ...(msg.tool_ui ? { toolUi: msg.tool_ui as A2UISurface } : {}),
          ...(msg.metadata ? { metadata: msg.metadata } : {}),
          ...(msg.thoughts && msg.thoughts.length > 0 ? { thoughts: msg.thoughts } : {}),
        }));
      setThreadSummary(response.summary || null);
      setGenerationStatus(response.generation_status || null);

      // Archive progressive loading state
      const archTotal = response.archive_total ?? 0;
      setArchiveTotalState(archTotal);

      // Restore cached archive messages from sessionStorage
      let cachedArchive: Message[] = [];
      let cachedOffset = 0;
      let cachedHasMore = archTotal > 0;
      try {
        const cached = sessionStorage.getItem(archiveCacheKey(threadId));
        const cachedMeta = sessionStorage.getItem(archiveMetaCacheKey(threadId));
        if (cached && cachedMeta) {
          const meta = JSON.parse(cachedMeta);
          cachedArchive = JSON.parse(cached).map((m: Message) => ({
            ...m,
            timestamp: new Date(m.timestamp),
          }));
          cachedOffset = meta.offset;
          cachedHasMore = meta.hasMore;
        }
      } catch {
        /* ignore parse errors */
      }

      setMessages([...cachedArchive, ...activeMessages]);
      setArchiveOffset(cachedOffset);
      setHasMoreArchive(cachedHasMore);

      setThreadStats(computeThreadStats(activeMessages, archTotal));

      // Update thread list message count
      setThreads((prev) =>
        prev.map((t) => (t.thread_id === threadId ? { ...t, message_count: activeMessages.length + archTotal } : t)),
      );
    } catch (err) {
      console.error('Failed to load messages:', err);
      setMessages([]);
      setThreadSummary(null);
      setThreadStats(null);
      setHasMoreArchive(false);
    }
  };

  const loadMoreArchive = async () => {
    if (!selectedThreadId || loadingArchive || !hasMoreArchive) return;
    setLoadingArchive(true);
    isLoadingArchiveRef.current = true;

    try {
      const response = await threadsApi.getMessages(selectedThreadId, {
        source: 'archive',
        order: 'asc',
        limit: 10,
        offset: archiveOffset,
      });
      const olderMessages: Message[] = response.messages
        .filter((msg) => ['human', 'user', 'ai', 'assistant'].includes(msg.role) && (msg.content || '').trim())
        .map((msg, index) => ({
          id: msg.id || `archive-load-${archiveOffset}-${index}`,
          role: msg.role === 'human' || msg.role === 'user' ? ('user' as const) : ('assistant' as const),
          content: (msg.content || '').trim(),
          timestamp: new Date(msg.timestamp),
          source: msg.source,
          ...(msg.tool_ui ? { toolUi: msg.tool_ui as A2UISurface } : {}),
          ...(msg.metadata ? { metadata: msg.metadata } : {}),
        }));

      const newOffset = archiveOffset + response.messages.length;
      const newHasMore = response.pagination.has_more;

      setMessages((prev) => {
        const updated = [...olderMessages, ...prev];
        // Cache all archive messages to sessionStorage
        const archiveMsgs = updated.filter((m) => m.source === 'archive');
        try {
          sessionStorage.setItem(archiveCacheKey(selectedThreadId!), JSON.stringify(archiveMsgs));
          sessionStorage.setItem(
            archiveMetaCacheKey(selectedThreadId!),
            JSON.stringify({ offset: newOffset, hasMore: newHasMore }),
          );
        } catch {
          /* quota exceeded, ignore */
        }
        return updated;
      });
      setArchiveOffset(newOffset);
      setHasMoreArchive(newHasMore);

      // Scroll preservation is handled by VirtualizedMessageList
      requestAnimationFrame(() => {
        isLoadingArchiveRef.current = false;
      });
    } catch (err) {
      console.error('Failed to load archive messages:', err);
      isLoadingArchiveRef.current = false;
    } finally {
      setLoadingArchive(false);
    }
  };

  // Delete handlers
  const handleDeleteUser = async (userId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('이 사용자를 삭제하시겠습니까? 모든 관련 데이터(스레드, 메시지, 메모리, 문서)가 삭제됩니다.')) return;

    try {
      setDeletingUserId(userId);
      await usersApi.delete(userId);
      if (selectedUserId === userId) {
        setSelectedUserId(null);
        setSelectedUser(null);
        setMessages([]);
        tabData.setMemories([]);
        setThreads([]);
      }
      await loadUsers();
      refetchStats();
    } catch (err) {
      console.error('Delete user error:', err);
      alert(`Failed to delete user: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setDeletingUserId(null);
    }
  };

  // Clone (debug). Enabled in user-management too (real 유저를 복제해 디버깅) — clone is
  // tagged playground + this admin, so it lands in the playground workspace, not this list.
  const handleCloneUser = async (userId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!adminUser?.id) return;
    if (!confirm('이 사용자를 복제하시겠습니까?\n대화·메모리·문서·체크포인트 전체를 복제하며, 데이터가 많으면 수십 초 걸릴 수 있습니다.\n복제본은 플레이그라운드에 생성됩니다.')) return;
    try {
      setCloningUserId(userId);
      const result = await usersApi.clone(userId, adminUser.id);
      alert(`복제 완료 — 플레이그라운드에 생성되었습니다.\n새 user_id: ${result.user_id}`);
    } catch (err) {
      console.error('Clone user error:', err);
      alert(`복제 실패: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setCloningUserId(null);
    }
  };

  const handleDeleteThread = async (threadId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('이 대화 스레드를 삭제하시겠습니까?')) return;

    try {
      await threadsApi.delete(threadId);
    } catch (err) {
      console.warn('Thread may have been already deleted:', err);
    }

    if (selectedThreadId === threadId) {
      setSelectedThreadId(null);
      setMessages([]);
    }
    if (selectedUserId) await loadThreads(selectedUserId);
  };

  const getUserName = (user: UserListItem | UserDetail) => {
    return user.user?.name || 'Anonymous';
  };

  return (
    <div className="h-[calc(100vh-7rem)] flex gap-4">
      {/* Left Sidebar - User & Thread List */}
      <div className="w-80 bg-white rounded-xl border border-slate-200 flex flex-col overflow-hidden shrink-0">
        {/* User section header */}
        <div className="p-4 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800">사용자 관리</h3>
            <div className="flex gap-2">
              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-[10px] font-medium flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                {stats?.active_users_24h || 0}
              </span>
              <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-[10px] font-medium">
                {stats?.total_users?.toLocaleString() || 0} total
              </span>
              <button
                onClick={() => loadUsers()}
                data-testid="refresh-users"
                className="p-1.5 text-slate-500 hover:bg-slate-200 rounded-lg transition-colors"
                title="새로고침"
              >
                <RefreshCw size={14} className={usersLoading ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="ID 또는 이름으로 검색..."
              data-testid="user-search-input"
              className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
            />
          </div>
          {/* Source filter */}
          <div className="flex gap-1 mt-2">
            {(
              [
                ['real', 'Real'],
                ['playground', 'PG'],
                ['all', 'All'],
              ] as const
            ).map(([val, label]) => (
              <button
                key={val}
                onClick={() => setSourceFilter(val)}
                className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
                  sourceFilter === val ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* User list */}
        <div className="flex-1 overflow-y-auto" style={{ maxHeight: selectedUserId ? '40%' : undefined }}>
          {usersLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={24} className="animate-spin text-slate-400" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <UserIcon size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">사용자가 없습니다</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {users.map((user) => (
                <div
                  key={user.user_id}
                  data-testid="user-item"
                  onClick={() => handleSelectUser(user.user_id)}
                  className={`p-3 cursor-pointer transition-colors group ${
                    selectedUserId === user.user_id ? 'bg-blue-50 border-l-2 border-blue-500' : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${getTierStyle(user.tier?.id)}`}>
                          {user.tier?.id || 'free'}
                        </span>
                        {user.is_playground && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-600 font-medium shrink-0">
                            PG
                          </span>
                        )}
                        <span className="flex items-center gap-1 text-xs text-slate-400">
                          <Clock size={10} />
                          {formatDateKo(user.updated_at)}
                        </span>
                      </div>
                      <div className="font-medium text-sm text-slate-800 truncate">{getUserName(user)}</div>
                      <div className="font-mono text-[10px] text-slate-500 break-all leading-tight">{user.user_id}</div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Brain size={10} />
                          {user.usage?.memory_count || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText size={10} />
                          {user.usage?.document_count || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <Hash size={10} />
                          {user.usage?.thread_count || 0}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => handleCloneUser(user.user_id, e)}
                        data-testid="clone-user"
                        className="p-1 text-slate-300 hover:text-green-600"
                        title="복제 (디버깅용 — 플레이그라운드에 생성)"
                      >
                        {cloningUserId === user.user_id ? (
                          <Loader2 size={14} className="animate-spin text-green-600" />
                        ) : (
                          <Copy size={14} />
                        )}
                      </button>
                      <button
                        onClick={(e) => handleDeleteUser(user.user_id, e)}
                        data-testid="delete-user"
                        className="p-1 text-slate-300 hover:text-red-500"
                        title="삭제"
                      >
                        {deletingUserId === user.user_id ? (
                          <Loader2 size={14} className="animate-spin text-red-500" />
                        ) : (
                          <Trash2 size={14} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Thread section - shown when user is selected */}
        {selectedUserId && (
          <>
            <div className="p-3 border-t border-b border-slate-100 bg-indigo-50 flex items-center justify-between">
              <h4 className="font-semibold text-sm text-indigo-800 flex items-center gap-1">
                <MessageSquare size={14} />
                대화 스레드
              </h4>
              <span className="text-xs text-indigo-500">{threads.length}개</span>
            </div>
            <div className="flex-1 overflow-y-auto">
              {threadsLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 size={18} className="animate-spin text-slate-400" />
                </div>
              ) : threads.length === 0 ? (
                <div className="text-center py-4 text-slate-400">
                  <p className="text-xs">스레드가 없습니다</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {threads.map((thread) => (
                    <div
                      key={thread.thread_id}
                      data-testid="thread-item"
                      onClick={() => setSelectedThreadId(thread.thread_id)}
                      className={`px-3 py-2 cursor-pointer transition-colors group ${
                        selectedThreadId === thread.thread_id
                          ? 'bg-indigo-50 border-l-2 border-indigo-500'
                          : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-slate-700 truncate">{thread.title || '제목 없음'}</div>
                          <div className="font-mono text-[10px] text-slate-400 break-all leading-tight">
                            {thread.thread_id}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5 flex-wrap">
                            {selectedThreadId === thread.thread_id && threadStats ? (
                              <>
                                <span title={`전체 ${threadStats.totalTurns} / 활성 ${threadStats.activeTurns} 턴`}>
                                  {threadStats.activeTurns}/{threadStats.totalTurns} turns
                                </span>
                                <span title={`활성 ${threadStats.activeTokens.toLocaleString()} tokens`}>
                                  {threadStats.activeTokens.toLocaleString()} tok
                                </span>
                                <span title="마지막 메시지 이후 경과 시간">{threadStats.idleHours}h idle</span>
                              </>
                            ) : (
                              <span>{thread.message_count} messages</span>
                            )}
                            {thread.last_message_at && <span>{formatDateKo(thread.last_message_at)}</span>}
                          </div>
                        </div>
                        <button
                          onClick={(e) => handleDeleteThread(thread.thread_id, e)}
                          className="p-1 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="삭제"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Pagination */}
        <div className="p-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
          <span className="text-xs text-slate-500">
            {total > 0 ? `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, total)} / ${total}` : '0 users'}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="p-1 border border-slate-200 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="text-xs text-slate-600 px-1">
              {page}/{totalPages || 1}
            </span>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              className="p-1 border border-slate-200 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden">
        {!selectedUserId ? (
          <div className="flex-1 flex items-center justify-center text-slate-400">
            <div className="text-center">
              <UserIcon size={48} className="mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">사용자를 선택하세요</p>
              <p className="text-sm mt-1">좌측 목록에서 사용자를 선택하여 상세 정보를 확인하세요</p>
            </div>
          </div>
        ) : (
          <>
            <TabBar
              activeTab={activeTab}
              onTabChange={(tab) => {
                setActiveTab(tab);
                if (tab === 'memories' && selectedUserId) tabData.loadUserMemories(selectedUserId);
                if (tab === 'documents' && selectedUserId) tabData.loadUserDocuments(selectedUserId);
                if (tab === 'health' && selectedUserId) tabData.loadHealthAnalyses(selectedUserId);
                if (tab === 'info' && selectedUserId) loadAvailableTools();
              }}
              memoriesCount={tabData.memories.length}
              documentsCount={tabData.documents.length}
              selectedUserId={selectedUserId}
              selectedThreadId={selectedThreadId}
            />

            <ErrorBanner error={error} onDismiss={() => setError(null)} />

            {/* Chat Tab (read-only) */}
            {activeTab === 'chat' && (
              <div className="flex-1 flex flex-col overflow-hidden">
                <VirtualizedMessageList
                  messages={messages}
                  hasMoreArchive={hasMoreArchive}
                  loadingArchive={loadingArchive}
                  onLoadMoreArchive={loadMoreArchive}
                  onCollapseArchive={() => {
                    setMessages((prev) => prev.filter((m) => m.source !== 'archive'));
                    setArchiveOffset(0);
                    setHasMoreArchive(true);
                    if (selectedThreadId) {
                      try {
                        sessionStorage.removeItem(archiveCacheKey(selectedThreadId));
                        sessionStorage.removeItem(archiveMetaCacheKey(selectedThreadId));
                      } catch {}
                    }
                  }}
                  threadSummary={threadSummary}
                  summaryExpanded={summaryExpanded}
                  onToggleSummary={() => setSummaryExpanded(!summaryExpanded)}
                  shouldAutoScroll={!isLoadingArchiveRef.current}
                  emptyTitle="대화 내역이 없습니다"
                  emptySubtitle={
                    selectedThreadId
                      ? '이 스레드에 메시지가 없습니다'
                      : '스레드를 선택하여 대화 내역을 확인하세요'
                  }
                  renderMessage={(msg) => (
                    <>
                      <ChatMessageBubble
                        msg={msg}
                        a2uiElement={
                          msg.toolUi ? (
                            <A2UISurfaceRenderer surface={msg.toolUi} isInteractive={false} />
                          ) : undefined
                        }
                      />
                      {msg.role === 'assistant' && msg.thoughts && msg.thoughts.length > 0 && (
                        <div className="ml-14 mt-1">
                          <ThoughtsPanel thoughts={msg.thoughts} />
                        </div>
                      )}
                    </>
                  )}
                />
              </div>
            )}

            {/* User Info Tab */}
            {activeTab === 'info' && selectedUser && (
              <InfoTabPanel
                user={selectedUser}
                profile={profile}
                tier={tier}
                threadMetadata={threadDetail?.metadata ?? null}
                extraSections={
                  <AvailableToolsSection
                    tools={availableTools}
                    loading={toolsLoading}
                    onRefresh={loadAvailableTools}
                  />
                }
              />
            )}

            {/* Thread Info Tab */}
            {activeTab === 'thread' && selectedThreadId && (
              <ThreadInfoPanel
                threadDetail={threadDetail ?? null}
                threadDetailLoading={threadDetailLoading}
                threadStats={threadStats}
                threadSummary={threadSummary}
                generationStatus={generationStatus}
                archiveTotal={archiveTotalState}
                onRefresh={() => refetchThreadDetail()}
              />
            )}

            {/* Memories Tab */}
            {activeTab === 'memories' && (
              <MemoriesTabPanel
                memories={tabData.memories}
                loading={tabData.memoriesLoading}
                onRefresh={() => selectedUserId && tabData.loadUserMemories(selectedUserId)}
                onDelete={tabData.handleDeleteMemory}
                onUpdate={tabData.handleUpdateMemory}
                history={tabData.memoryHistory}
                historyLoading={tabData.memoryHistoryLoading}
                onLoadHistory={() => selectedUserId && tabData.loadMemoryHistory(selectedUserId)}
              />
            )}

            {/* Documents Tab */}
            {activeTab === 'documents' && (
              <DocumentsTabPanel
                documents={tabData.documents}
                loading={tabData.documentsLoading}
                onRefresh={() => selectedUserId && tabData.loadUserDocuments(selectedUserId)}
                onDelete={tabData.handleDeleteDocument}
                onToggle={tabData.handleToggleDocument}
                expandedDocumentId={tabData.expandedDocumentId}
                expandedDocumentDetail={tabData.expandedDocumentDetail}
                loadingDocumentDetail={tabData.loadingDocumentDetail}
                onToggleDetail={(docId) => selectedUserId && tabData.loadDocumentDetail(selectedUserId, docId)}
              />
            )}

            {/* Health Analysis Tab */}
            {activeTab === 'health' && (
              <HealthTabPanel
                analyses={tabData.healthAnalyses}
                loading={tabData.healthAnalysisLoading}
                expandedAnalysisId={tabData.expandedAnalysisId}
                expandedAnalysisDetail={tabData.expandedAnalysisDetail}
                loadingAnalysisDetail={tabData.loadingAnalysisDetail}
                onRefresh={() => selectedUserId && tabData.loadHealthAnalyses(selectedUserId)}
                onToggleDetail={(analysisId) => selectedUserId && tabData.loadAnalysisDetail(selectedUserId, analysisId)}
                onDelete={tabData.handleDeleteHealthAnalysis}
              />
            )}

            {/* Calendar Tab */}
            {activeTab === 'calendar' && selectedUserId && (
              <CalendarTabPanel userId={selectedUserId} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AgentUsers;
